import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Alert, App as AntApp, Button, Input, Modal, Space, Spin, Typography, Upload } from 'antd';
import { FilePdfOutlined, UploadOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

const { Text } = Typography;
const JOB_KEY = 'licitacoes_inclusao_edital_oportunidade_id';
const UPLOAD_KEY = 'licitacoes_inclusao_edital_upload_pendente';
const MAX_PDF_BYTES = 15 * 1024 * 1024;
const POLL_MS = 4000;

type UploadPendente = { s3_key: string; filename: string; link_pncp: string };

const FASES: Record<string, string> = {
  recebido: 'Edital recebido',
  extracao: 'Lendo o PDF',
  metadata: 'Organizando os dados da oportunidade',
  metadados: 'Organizando os dados da oportunidade',
  pncp: 'Consultando o PNCP',
  documentos: 'Reunindo os documentos',
  analises: 'Preparando as análises',
  concluido: 'Montagem concluída',
};

function nomeDaFase(fase?: string | null): string {
  return fase ? (FASES[fase.toLowerCase()] || fase) : 'Aguardando próxima etapa';
}

type InclusaoStatus = {
  id?: number;
  status: string;
  fase?: string | null;
  mensagem?: string | null;
  avisos?: string[] | null;
  erro?: string | null;
  oportunidade_id?: number | null;
};

function lerJob(): number | null {
  try {
    const id = Number(localStorage.getItem(JOB_KEY));
    return Number.isSafeInteger(id) && id > 0 ? id : null;
  } catch { return null; }
}

function guardarJob(id: number | null) {
  try {
    if (id) localStorage.setItem(JOB_KEY, String(id));
    else localStorage.removeItem(JOB_KEY);
  } catch { /* O acompanhamento segue nesta aba mesmo sem armazenamento local. */ }
}

function lerUpload(): UploadPendente | null {
  try {
    const valor = localStorage.getItem(UPLOAD_KEY);
    if (!valor) return null;
    const d = JSON.parse(valor);
    return typeof d.s3_key === 'string' && typeof d.filename === 'string'
      ? { s3_key: d.s3_key, filename: d.filename, link_pncp: String(d.link_pncp || '') }
      : null;
  } catch { return null; }
}

function guardarUpload(upload: UploadPendente | null) {
  try {
    if (upload) localStorage.setItem(UPLOAD_KEY, JSON.stringify(upload));
    else localStorage.removeItem(UPLOAD_KEY);
  } catch { /* A repetição na mesma janela ainda reutiliza o upload. */ }
}

async function post<T>(body: Record<string, unknown>): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/licitacoes`, body,
    { headers: { Authorization: token }, timeout: 25000 });
  return (typeof r.data?.body === 'string' ? JSON.parse(r.data.body) : r.data) as T;
}

function erroDe(e: any): string {
  return e?.response?.data?.error || e?.response?.data?.erro || e?.message || 'Erro desconhecido';
}

function terminou(s: InclusaoStatus | null): boolean {
  return !!s && ['pronto', 'concluido', 'concluida', 'finalizado', 'finalizada'].includes(s.status);
}

function falhou(s: InclusaoStatus | null): boolean {
  return !!s && ['erro', 'falhou', 'falha'].includes(s.status);
}

export default function ModalIncluirEdital({ open, onClose, podeOperar, podeVerPipeline }: {
  open: boolean; onClose: () => void; podeOperar: boolean; podeVerPipeline: boolean;
}) {
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [uploadConcluido, setUploadConcluido] = useState<UploadPendente | null>(lerUpload);
  const [pncpUrl, setPncpUrl] = useState(() => lerUpload()?.link_pncp || '');
  const [enviando, setEnviando] = useState(false);
  const [faseEnvio, setFaseEnvio] = useState('');
  const [jobId, setJobId] = useState<number | null>(lerJob);
  const [status, setStatus] = useState<InclusaoStatus | null>(null);
  const [consultando, setConsultando] = useState(false);
  const [consultaErro, setConsultaErro] = useState('');
  const [retomando, setRetomando] = useState(false);
  const [jobNaoEncontrado, setJobNaoEncontrado] = useState(false);
  const emEnvio = useRef(false);
  const emConsulta = useRef(false);

  const consultar = useCallback(async (id: number) => {
    if (emConsulta.current) return;
    emConsulta.current = true;
    setConsultando(true);
    try {
      const novo = await post<InclusaoStatus>({ action: 'inclusao_edital_status', oportunidade_id: id });
      setStatus(novo);
      setConsultaErro('');
      setJobNaoEncontrado(false);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setJobNaoEncontrado(true);
        setConsultaErro('Este acompanhamento não foi encontrado para a sua conta. Você pode descartá-lo e incluir outro edital.');
      } else setConsultaErro('Não consegui consultar o andamento: ' + erroDe(e));
    } finally {
      emConsulta.current = false;
      setConsultando(false);
    }
  }, []);

  // O identificador fica no navegador. Reabrir o modal ou recarregar a página
  // recupera o estado real do servidor, inclusive erros e avisos.
  useEffect(() => {
    if (!open || !jobId || jobNaoEncontrado || terminou(status) || falhou(status)) return;
    consultar(jobId);
    const timer = window.setInterval(() => consultar(jobId), POLL_MS);
    return () => window.clearInterval(timer);
  }, [open, jobId, jobNaoEncontrado, status?.status, consultar]); // eslint-disable-line react-hooks/exhaustive-deps

  async function montar() {
    if (emEnvio.current || !podeOperar || (!arquivo && !uploadConcluido) || jobId) return;
    emEnvio.current = true;
    setEnviando(true);
    setConsultaErro('');
    try {
      let pendente = uploadConcluido;
      if (!pendente && arquivo) {
        setFaseEnvio('Preparando o envio do PDF…');
        const dados = await post<{ upload_url: string; s3_key: string }>({
          action: 'upload_url_incluir_edital', filename: arquivo.name,
          content_type: 'application/pdf', tamanho_bytes: arquivo.size,
        });
        setFaseEnvio('Enviando o PDF…');
        await axios.put(dados.upload_url, arquivo,
          { headers: { 'Content-Type': 'application/pdf' } });
        pendente = { s3_key: dados.s3_key, filename: arquivo.name, link_pncp: pncpUrl.trim() };
        guardarUpload(pendente);
        setUploadConcluido(pendente);
      }
      if (!pendente) throw new Error('Selecione o PDF do edital.');
      setFaseEnvio('Iniciando a montagem…');
      const criado = await post<InclusaoStatus>({
        action: 'incluir_edital', s3_key: pendente.s3_key,
        ...(pncpUrl.trim() ? { link_pncp: pncpUrl.trim() } : {}),
      });
      const id = Number(criado.oportunidade_id);
      if (!Number.isSafeInteger(id) || id <= 0) throw new Error('O servidor não informou o número do processamento.');
      guardarJob(id);
      guardarUpload(null);
      setUploadConcluido(null);
      setJobId(id);
      setStatus(criado);
      setArquivo(null);
    } catch (e: any) {
      setConsultaErro('Não consegui confirmar a inclusão: ' + erroDe(e)
        + (uploadConcluido || lerUpload() ? ' Tente novamente; o PDF já enviado será reutilizado.' : ''));
    } finally {
      emEnvio.current = false;
      setEnviando(false);
      setFaseEnvio('');
    }
  }

  async function retomar() {
    if (!podeOperar || !jobId || retomando) return;
    setRetomando(true);
    try {
      await post({ action: 'inclusao_edital_retomar', oportunidade_id: jobId });
      setConsultaErro('');
      setStatus({ id: jobId, status: 'processando', fase: 'Retomando' });
      consultar(jobId);
    } catch (e: any) {
      setConsultaErro('Não consegui tentar novamente: ' + erroDe(e));
    } finally { setRetomando(false); }
  }

  function abrirOportunidade() {
    const id = Number(status?.oportunidade_id);
    if (!Number.isSafeInteger(id) || id <= 0) return;
    guardarJob(null);
    guardarUpload(null);
    setUploadConcluido(null);
    setJobId(null);
    setStatus(null);
    onClose();
    navigate(podeVerPipeline
      ? `/comercial/licitacoes?aba=pipeline&licitacao=${id}`
      : `/comercial/licitacoes?aba=oportunidades&oportunidade=${id}`);
  }

  function fechar() {
    if (enviando) return;
    onClose();
  }

  function novoEdital() {
    guardarJob(null);
    guardarUpload(null);
    setUploadConcluido(null);
    setJobId(null);
    setStatus(null);
    setConsultaErro('');
    setJobNaoEncontrado(false);
    setArquivo(null);
    setPncpUrl('');
    if (!podeOperar) onClose();
  }

  const pronta = terminou(status);
  const comErro = falhou(status);
  const podeAbrir = pronta && !!status?.oportunidade_id;

  return (
    <Modal open={open} onCancel={fechar} footer={null} maskClosable={!enviando} closable={!enviando}
      title="Incluir edital" width="94vw" style={{ maxWidth: 760, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}>
      {!jobId ? (
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <Text>Envie o edital em PDF. O sistema buscará os dados públicos disponíveis e montará a oportunidade para você conferir.</Text>
          <Upload.Dragger accept=".pdf,application/pdf" maxCount={1} disabled={enviando}
            beforeUpload={(file) => {
              if (!/\.pdf$/i.test(file.name)) {
                message.error('Selecione um PDF.'); return Upload.LIST_IGNORE;
              }
              if (file.size > MAX_PDF_BYTES) {
                message.error('O PDF deve ter até 15 MB.'); return Upload.LIST_IGNORE;
              }
              setArquivo(file as File);
              guardarUpload(null);
              setUploadConcluido(null);
              return false;
            }}
            onRemove={() => { setArquivo(null); guardarUpload(null); setUploadConcluido(null); return true; }}
            fileList={arquivo ? [{ uid: 'edital', name: arquivo.name, status: 'done' }] : []}>
            <FilePdfOutlined style={{ fontSize: 30, color: '#0930D0' }} />
            <p>Escolher ou arrastar o PDF do edital</p>
            <Text type="secondary">Até 15 MB</Text>
          </Upload.Dragger>
          {uploadConcluido && <Alert type="info" showIcon
            message={`PDF já enviado: ${uploadConcluido.filename}`}
            description="Se a resposta anterior se perdeu, tente novamente. O mesmo arquivo será reutilizado sem outro envio." />}
          <div>
            <Text strong>Link do PNCP (opcional)</Text>
            <Input value={pncpUrl} onChange={e => setPncpUrl(e.target.value)} disabled={enviando}
              placeholder="https://pncp.gov.br/app/editais/…" type="url" />
            <Text type="secondary" style={{ fontSize: 12 }}>Se souber o link, ele ajuda a buscar documentos e atualizações.</Text>
          </div>
          {consultaErro && <Alert type="error" showIcon message={consultaErro} />}
          {enviando && <Alert type="info" showIcon message={faseEnvio} />}
          <Space style={{ width: '100%', justifyContent: 'flex-end' }} wrap>
            <Button onClick={fechar} disabled={enviando}>Cancelar</Button>
            <Button type="primary" icon={<UploadOutlined />} onClick={montar}
              loading={enviando} disabled={!arquivo && !uploadConcluido}>
              {uploadConcluido ? 'Tentar concluir inclusão' : 'Montar oportunidade'}
            </Button>
          </Space>
        </Space>
      ) : (
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <Alert type={comErro ? 'error' : pronta ? 'success' : 'info'} showIcon
            message={comErro ? 'A montagem parou' : pronta ? 'Oportunidade pronta para conferir' : 'Montando oportunidade'}
            description={status?.erro || status?.mensagem || (consultando ? 'Consultando andamento…' : 'O processamento continua em segundo plano.')} />
          {!pronta && !comErro && <Space><Spin size="small" /><Text>Buscando e analisando os dados do edital…</Text></Space>}
          <Text>Etapa: {nomeDaFase(status?.fase)}</Text>
          {status?.avisos && status.avisos.length > 0 && (
            <Alert type="warning" showIcon message="Pontos para conferir"
              description={<ul style={{ margin: 0, paddingLeft: 20 }}>{status.avisos.map((aviso, i) => <li key={i}>{aviso}</li>)}</ul>} />
          )}
          {consultaErro && <Alert type="warning" showIcon message={consultaErro}
            description="O processamento pode continuar no servidor. Você pode fechar esta janela e voltar para consultar." />}
          <Text type="secondary" style={{ fontSize: 12 }}>
            Você pode fechar esta janela. Ao voltar a Licitações, o andamento será recuperado.
            A participação e a proposta dependem de decisão da equipe.
          </Text>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }} wrap>
            <Button onClick={fechar}>Fechar</Button>
            {comErro && podeOperar && <Button onClick={retomar} loading={retomando}>Tentar novamente</Button>}
            {podeAbrir && <Button type="primary" onClick={abrirOportunidade}>Abrir oportunidade</Button>}
            {pronta && !podeAbrir && <Button onClick={() => consultar(jobId)}>Atualizar status</Button>}
            {pronta && podeOperar && <Button onClick={novoEdital}>Incluir outro edital</Button>}
            {jobNaoEncontrado && <Button onClick={novoEdital}>
              {podeOperar ? 'Descartar e incluir outro' : 'Descartar acompanhamento'}
            </Button>}
          </Space>
        </Space>
      )}
    </Modal>
  );
}

export { lerJob as inclusaoEditalPendente };
